import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarRange, PenLine, Send, CalendarCheck, MessagesSquare, ListPlus, BarChart3, HelpCircle, ShieldCheck, Upload, Search, CheckCircle2, Circle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { StatusPill } from "@/components/common/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { GUARDRAIL, teamColour } from "@/lib/constants";
import { firstName, greeting } from "@/lib/format";
import { AnnouncementBanner, PersonalLayer } from "@/components/home/HomeSections";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { openPalette } from "@/components/layout/CommandPalette";
import { api } from "@/lib/api";
import { agentName } from "@/lib/catalogue";

const ICONS = {
  "Plan a campaign": CalendarRange, "Write and atomise": PenLine, "Send and publish": Send, "Run an event": CalendarCheck,
  "Handle enquiries and outreach": MessagesSquare, "Build a list": ListPlus, "Report and review": BarChart3, "Ask how we do things": HelpCircle,
};
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const TaskCard = ({ group }) => {
  const { agentsByKey, workflowsByKey, teamsById } = useCatalogue();
  const entries = group.items
    .map((k) => (workflowsByKey[k] ? { ...workflowsByKey[k], kind: "workflow", key: k } : agentsByKey[k] ? { ...agentsByKey[k], kind: "agent", key: k } : null))
    .filter(Boolean);
  const live = entries.filter((e) => e.live);
  const owners = [...new Set(entries.map((e) => e.team).filter(Boolean))].map((t) => teamsById[t]).filter(Boolean);
  const Icon = ICONS[group.name] || HelpCircle;
  const colour = teamColour(owners[0], owners[0]?._index);
  return (
    <article className="entry-card card-elevated flex flex-col p-6" style={{ "--team": colour }} data-testid={`task-card-${slug(group.name)}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full" style={{ background: `color-mix(in srgb, ${colour} 18%, transparent)`, color: colour }}><Icon aria-hidden="true" /></span>
        {live.length ? <StatusPill status="live" label={`${live.length} live`} /> : <StatusPill status="soon" />}
      </div>
      <h2 className="mt-4 text-base font-semibold">{group.name}</h2>
      {live.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {live.slice(0, 6).map((e) => {
            const nm = e.kind === "workflow" ? e.name : agentName(e);
            return (
              <li key={e.key}>
                <Link to={e.kind === "workflow" ? `/workflows/${e.key}` : `/agents/${e.key}`} className="inline-flex items-center gap-1.5 rounded-full border bg-card py-0.5 pl-0.5 pr-2.5 text-sm transition-quick hover:bg-muted" data-testid={`task-entry-${e.key}`}>
                  <EntityAvatar entity={e} name={nm} size="xs" />{nm}
                </Link>
              </li>
            );
          })}
          {live.length > 6 && <li className="self-center text-sm text-muted-foreground">and {live.length - 6} more</li>}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">{owners.length ? `Coming soon from ${owners.map((t) => t.section || t.name).join(", ")}.` : "Coming soon."}</p>
      )}
    </article>
  );
};

const SUGGESTIONS = [["Plan a campaign", "campaign"], ["Review a draft", "review"], ["Build a list", "list"]];

const Hero = ({ user }) => {
  const navigate = useNavigate();
  return (
    <section className="hero-panel relative overflow-hidden rounded-3xl p-6 text-white md:p-10" data-testid="home-hero">
      <p className="text-sm text-white/85" data-testid="home-greeting">{greeting()}, {firstName(user.name)}</p>
      <h1 className="mt-1 text-xl font-semibold md:text-2xl">What do you want to do?</h1>
      <button type="button" onClick={openPalette} className="mt-6 flex h-14 w-full max-w-2xl items-center gap-3 rounded-2xl bg-white px-5 text-left text-base text-slate-600 shadow-lg transition-quick hover:shadow-xl" data-testid="home-command-search">
        <Search className="text-slate-500" aria-hidden="true" /><span className="flex-1">Search agents, workflows, prompts and pages</span><kbd className="hidden rounded-md border px-2 py-0.5 font-mono text-xs sm:inline">Ctrl K</kbd>
      </button>
      <div className="mt-4 flex flex-wrap gap-2">
        {SUGGESTIONS.map(([l, q]) => <button key={q} type="button" onClick={() => navigate(`/agents?q=${q}`)} className="rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-sm text-white transition-quick hover:bg-white/20" data-testid={`home-suggestion-${q}`}>{l}</button>)}
      </div>
      <p className="mt-6 inline-flex items-center gap-2 text-sm text-white/85" data-testid="home-guardrail"><ShieldCheck className="!h-4 !w-4" aria-hidden="true" /> {GUARDRAIL}</p>
    </section>
  );
};

const StatsStrip = ({ stats }) => {
  const { agents, workflows } = useCatalogue();
  const liveCount = agents.filter((a) => a.live && !a.parent).length + workflows.filter((w) => w.live).length;
  const items = [["Agents live", liveCount, "stat-agents-live"], ["Runs this week", stats?.runs_this_week ?? "", "stat-runs-week"], ["Prompts shared", stats?.prompts_shared ?? "", "stat-prompts"]];
  return (
    <div className="mt-6 grid grid-cols-3 gap-3 md:gap-4" data-testid="home-stats">
      {items.map(([l, v, t]) => <div key={t} className="card-elevated p-4 md:p-5" data-testid={t}><p className="text-sm text-muted-foreground">{l}</p><p className="mt-1 text-xl font-semibold tabular-nums">{v}</p></div>)}
    </div>
  );
};

const CHECKS = [["defaults", "Set your defaults", "/profile"], ["run", "Run one agent", "/agents"], ["prompt", "Save one prompt", "/prompts"], ["feedback", "Give one piece of feedback", "/feedback"]];

const FirstWeek = ({ stats }) => {
  if (!stats?.new_user || CHECKS.every(([k]) => stats.checklist[k])) return null;
  const done = CHECKS.filter(([k]) => stats.checklist[k]).length;
  return (
    <section className="card-elevated mt-6 p-5" data-testid="first-week-checklist">
      <div className="flex items-center justify-between"><h2 className="text-base font-semibold">Your first week</h2><span className="text-sm tabular-nums text-muted-foreground">{done} of 4 done</span></div>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {CHECKS.map(([k, l, to]) => (
          <li key={k}><Link to={to} className="flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-quick hover:bg-muted" data-testid={`checklist-${k}`}>
            {stats.checklist[k] ? <CheckCircle2 className="!h-4 !w-4 text-success" aria-label="Done" /> : <Circle className="!h-4 !w-4 text-muted-foreground" aria-label="Not done" />}
            <span className={stats.checklist[k] ? "text-muted-foreground line-through" : ""}>{l}</span>
          </Link></li>
        ))}
      </ul>
    </section>
  );
};

export default function Home() {
  const { user } = useAuth();
  const { task_groups: groups, loading, error, reload, isEmpty } = useCatalogue();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/home/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  return (
    <div data-testid="home-page">
      <AnnouncementBanner />
      <Hero user={user} />
      <StatsStrip stats={stats} />
      <FirstWeek stats={stats} />

      <div className="mt-8">
        {error ? <ErrorState message={`We could not load the catalogue. ${error}`} onRetry={reload} /> : loading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
        ) : isEmpty ? (
          <EmptyState
            icon={Upload}
            title="The catalogue is empty"
            body={user.role === "owner" ? "Import the catalogue file to fill the hub with teams, agents and workflows." : "An Owner needs to import the catalogue before agents appear here."}
            action={user.role === "owner" && <Button asChild><Link to="/admin/import" data-testid="home-import-button">Import catalogue</Link></Button>}
            testid="home-empty-catalogue"
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="task-group-grid">
            {groups.map((g) => <TaskCard key={g.id} group={g} />)}
          </div>
        )}
      </div>

      <PersonalLayer />
    </div>
  );
}
